import { useEffect, useReducer, useRef, useState, type FormEvent } from "react";
import "../styles/markets.css";
import "../styles/us-estimate.css";
import { dbk_UsEstimateFields as UsEstimateFields } from "./UsEstimateFields";
import { dbk_canSubmitEstimate, dbk_decodeEstimateDraft, dbk_emptyEstimate, dbk_encodeEstimateDraft, dbk_estimateDelivery,
  dbk_estimateGroups, dbk_estimateGroup, dbk_estimatePayload, dbk_estimateReducer, dbk_estimateStorageKey, dbk_estimateTerms,
  dbk_fieldVisible, dbk_groupFields, dbk_sensitiveField, dbk_validateEstimate, type dbk_EstimateConfig, type dbk_EstimateState, type dbk_EstimateAction, type dbk_EstimateIssue } from "./usEstimateModel";

type Result = { public_id?: string; status: string; onboarding_path?: string; saved?: boolean;
  proposal: { title: string; notice: string; scope: string } };
const legacyStorageKey = "dynamicbooks.us-estimate.tab-draft.v1";
function initialState(config: dbk_EstimateConfig): dbk_EstimateState {
  try {
    const raw = window.sessionStorage.getItem(dbk_estimateStorageKey) ?? window.sessionStorage.getItem(legacyStorageKey);
    const draft = dbk_decodeEstimateDraft(config, raw);
    if (draft) return { ...dbk_emptyEstimate, ...draft };
    if (raw) return { ...dbk_emptyEstimate, values: {}, draftBlocked: true,
      issues: [{ field: "draft", message: "The saved draft is expired or uses a different form configuration. It has not been overwritten. Start a new request to continue." }] };
  } catch { /* Storage unavailable: the canonical in-memory form remains usable. */ }
  return { ...dbk_emptyEstimate, values: {} };
}
function Delivery() { return <aside className="us-estimate__delivery"><h3>How Your Services Are Delivered</h3><p>{dbk_estimateDelivery}</p></aside>; }

function EstimateForm({ config }: { config: dbk_EstimateConfig }) {
  const [state, dispatch] = useReducer((current: dbk_EstimateState, action: dbk_EstimateAction) => dbk_estimateReducer(current, action, config), config, initialState);
  const inFlight = useRef(false);
  const retry = useRef<{ body: string; key: string } | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const focusTarget = useRef<HTMLHeadingElement>(null);
  const previousEditor = useRef<number | null>(null);
  const busy = state.phase === "processing";
  useEffect(() => {
    if (state.draftBlocked) return;
    try {
      if (state.phase === "success") { window.sessionStorage.removeItem(dbk_estimateStorageKey); window.sessionStorage.removeItem(legacyStorageKey); }
      else window.sessionStorage.setItem(dbk_estimateStorageKey, dbk_encodeEstimateDraft(config, state));
    } catch { /* Do not make storage availability a prerequisite for requesting an estimate. */ }
  }, [config, state]);
  useEffect(() => {
    const group = state.editor?.group ?? null;
    if (group !== null) {
      const first = dbk_groupFields(config, group).find(field => dbk_fieldVisible(field, state.values));
      const target = first ? document.getElementById(`us-${first.key}`) : null;
      (target?.querySelector<HTMLElement>("input, select, textarea") ?? target)?.focus();
    } else if (previousEditor.current !== null) {
      document.getElementById(`us-review-edit-${previousEditor.current}`)?.focus();
    } else focusTarget.current?.focus();
    previousEditor.current = group;
    // Values must not retrigger focus while the customer types in an editor.
  }, [config, state.step, state.editor?.group]);
  function validateAction(action: dbk_EstimateAction) {
    dispatch(action);
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>('.us-estimate :is(input, select, textarea)[aria-invalid="true"]')?.focus();
    });
  }
  function change(key: string, value: string | string[]) { dispatch({ type: "change", key, value }); }
  function reset() {
    if (inFlight.current) return;
    try { window.sessionStorage.removeItem(dbk_estimateStorageKey); window.sessionStorage.removeItem(legacyStorageKey); } catch { /* no storage */ }
    retry.current = null; setResult(null); dispatch({ type: "reset" });
  }
  function errors(issues: dbk_EstimateIssue[]) {
    const first = issues.find(issue => config.fields.some(field => field.key === issue.field));
    if (first && !state.editor) dispatch({ type: "edit", group: dbk_estimateGroup(first.field) });
    dispatch({ type: "issues", issues });
    requestAnimationFrame(() => {
      const target = document.getElementById(first ? `us-${first.field}` : "us-estimate-errors");
      (target?.querySelector<HTMLElement>("input, select, textarea") ?? target)?.focus();
    });
  }
  async function submit(event: FormEvent, preview = false) {
    event.preventDefault();
    if (inFlight.current || busy || state.phase === "success" || state.draftBlocked || state.editor || state.step !== 4) return;
    const issues = dbk_validateEstimate(config, state.values);
    if (!state.consent) issues.push({ field: "consent", message: "Please read and agree to the Commercial Terms." });
    if (issues.length) { errors(issues); return; }
    const body = JSON.stringify(dbk_estimatePayload(config, state.values, state.consent));
    if (!retry.current || retry.current.body !== body) {
      const bytes = crypto.getRandomValues(new Uint8Array(32));
      retry.current = { body, key: Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("") };
    }
    // Synchronous latch closes the same-render/double-event gap before React commits.
    inFlight.current = true; dispatch({ type: "processing" }); setResult(null);
    try {
      const response = await fetch(`/api/public/markets/US/intake${preview ? "/validate" : ""}`, {
        method: "POST", credentials: "omit", headers: { "Content-Type": "application/json", ...(preview ? {} : { "Idempotency-Key": retry.current.key }) }, body });
      const payload = await response.json();
      if (!response.ok || !payload.ok) {
        const issues = Array.isArray(payload.error?.issues) ? payload.error.issues : [{ field: "form", message: payload.error?.message ?? "The request could not be saved. Please try again." }];
        dispatch({ type: "failure", issues }); return;
      }
      const data: Result = payload.data;
      if (!data?.proposal || (preview ? data.status !== "draft_validated" || data.saved !== false :
        !["draft_received", "draft_reviewed"].includes(data.status) || typeof data.public_id !== "string" ||
        typeof data.onboarding_path !== "string" || !/^\/us\/proposal-onboarding\/dbon_[A-Za-z0-9_-]{32,160}$/.test(data.onboarding_path))) {
        dispatch({ type: "failure", issues: [{ field: "form", message: "The server did not confirm the request. Your answers are still here; please try again." }] }); return;
      }
      setResult(data);
      dispatch(preview ? { type: "failure", issues: [] } : { type: "success" });
    } catch { dispatch({ type: "failure", issues: [{ field: "form", message: "The request could not be completed. Your answers are still here; please try again." }] }); }
    finally { inFlight.current = false; }
  }
  if (state.phase === "success" && result) return <section className="us-estimate" role="status">
    <h2>Your estimate request has been received</h2><p>{result.proposal.notice}</p><p>{result.proposal.scope}</p>
    <p>This is an estimate request, not a purchase or acceptance of final pricing. No account, engagement, subscription or payment has been activated.</p>
    <a href={result.onboarding_path}>Review your US request</a><p><button type="button" onClick={reset}>Start a new request</button></p>
  </section>;
  return <section id="us-intake" className="db-panel market-intake us-estimate" aria-label="Request estimate form">
    <p>Describe your bookkeeping needs. This request produces a draft for review; fees and service availability are confirmed separately.</p>
    <ol className="us-estimate__progress" aria-label="Estimate progress">{[...dbk_estimateGroups, "Review & Submit"].map((title, index) =>
      <li key={title} aria-current={state.step === index ? "step" : undefined}><span>{index + 1}</span>{title}</li>)}</ol>
    <h2 ref={focusTarget} tabIndex={-1}>{state.step === 4 ? "Review & Submit" : dbk_estimateGroups[state.step]}</h2>
    <p className="us-estimate__help" role="status">Step {state.step + 1} of 5{busy ? " — Processing your request…" : ""}</p>
    {state.draftBlocked && <button type="button" onClick={reset}>Start a new request</button>}
    <form noValidate onSubmit={event => {
      if (state.step < 4) { event.preventDefault(); validateAction({ type: "navigate", step: state.step + 1 }); }
      else if (state.editor) { event.preventDefault(); validateAction({ type: "save" }); }
      else void submit(event);
    }}>
      <fieldset disabled={busy || state.draftBlocked}><legend className="us-estimate__sr-only">Estimate request answers</legend>
        {state.step < 4 ? <>
          {state.step === 0 && <p>* Required fields</p>}
          <UsEstimateFields fields={dbk_groupFields(config, state.step)} values={state.values} issues={state.issues} onChange={change} />
          {state.step === 3 && <Delivery />}
        </> : <>
          {dbk_estimateGroups.map((title, group) => {
            const editing = state.editor?.group === group;
            return <section key={title} className="us-estimate__review-group" aria-labelledby={`us-review-${group}`}>
              <header><h3 id={`us-review-${group}`}>{title}</h3>
                <button id={`us-review-edit-${group}`} type="button" aria-label={`Edit ${title}`} aria-expanded={editing} aria-controls={`us-review-fields-${group}`}
                  disabled={!!state.editor} onClick={() => dispatch({ type: "edit", group })}>Edit</button>
              </header>
              <div id={`us-review-fields-${group}`}>{editing ? <>
                <UsEstimateFields fields={dbk_groupFields(config, group)} values={state.values} issues={state.issues} onChange={change} />
                <div className="us-estimate__actions"><button type="button" onClick={() => validateAction({ type: "save" })}>Save changes</button>
                  <button type="button" onClick={() => dispatch({ type: "cancel" })}>Cancel</button></div>
              </> : <dl>{dbk_groupFields(config, group).filter(field => dbk_fieldVisible(field, state.values)).map(field => {
                const raw = state.values[field.key];
                const summary = dbk_sensitiveField(field) ? (raw ? "Provided — not retained or transmitted" : "Not provided") :
                  Array.isArray(raw) ? raw.map(value => field.options?.find(o => o.value === value)?.label ?? value).join(", ") : field.options?.find(o => o.value === raw)?.label ?? raw;
                return <div key={field.key}><dt>{field.label}</dt><dd>{summary || "Not provided"}</dd></div>;
              })}</dl>}</div>
              {!editing && dbk_validateEstimate(config, state.values, group).map((issue, index) => <p key={index} className="us-estimate__error">{issue.message} Use Edit to correct this group.</p>)}
              {group === 3 && <Delivery />}
            </section>;
          })}
          <div className="us-estimate__agreement"><label htmlFor="us-estimate-consent">
            <input id="us-estimate-consent" type="checkbox" checked={state.consent} aria-invalid={state.issues.some(i => i.field === "consent")}
              aria-describedby={state.issues.some(i => i.field === "consent") ? "us-consent-error" : undefined} onChange={event => dispatch({ type: "consent", value: event.target.checked })} />
            <span>I have read and agree to the <a href={dbk_estimateTerms} target="_blank" rel="noopener noreferrer">DynamicMail Commercial Terms</a>.</span>
          </label>{state.issues.find(i => i.field === "consent") && <p id="us-consent-error" className="us-estimate__error">Please read and agree to the Commercial Terms.</p>}</div>
          <p>Submitting requests an estimate. Final scope, pricing and any purchase or engagement require separate confirmation.</p>
        </>}
      </fieldset>
      {!!state.issues.length && <div id="us-estimate-errors" tabIndex={-1} role="alert"><p>Please check the highlighted answers.</p>
        <ul>{state.issues.map((issue, index) => <li key={index}>{issue.message}</li>)}</ul></div>}
      {state.editor && <p role="status">Save changes or Cancel before submitting your request.</p>}
      <div className="us-estimate__actions">
        {state.step > 0 && <button type="button" disabled={busy || !!state.editor || state.draftBlocked} onClick={() => dispatch({ type: "navigate", step: state.step - 1 })}>Back</button>}
        <button type="submit" className="db-button db-button--primary" disabled={busy || !!state.editor || state.draftBlocked || (state.step === 4 && !dbk_canSubmitEstimate(state, config))}>
          {busy ? "Processing…" : state.step < 4 ? "Continue" : "Submit estimate request"}</button>
        {state.step === 4 && <button type="button" disabled={!dbk_canSubmitEstimate(state, config)} onClick={event => void submit(event, true)}>Preview without saving</button>}
      </div>
    </form>
    {result?.saved === false && <section aria-live="polite"><h3>{result.proposal.title}</h3><p>{result.proposal.notice}</p><p>Preview only — no request has been saved.</p></section>}
  </section>;
}
export function dbk_UsEstimateForm() {
  const [config, setConfig] = useState<dbk_EstimateConfig | null>(null);
  const [error, setError] = useState("");
  useEffect(() => { let alive = true;
    fetch("/api/public/markets/US/intake-config", { credentials: "omit" }).then(async response => {
      const payload = await response.json();
      if (!response.ok || !payload.ok || payload.data?.country_code !== "US" || !Array.isArray(payload.data.fields)) throw new Error("unavailable");
      if (alive) setConfig(payload.data);
    }).catch(() => { if (alive) setError("The form could not load. Please try again."); });
    return () => { alive = false; };
  }, []);
  return config ? <EstimateForm config={config} /> : <p role="status">{error || "Loading US intake…"}</p>;
}
